import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";

// Searchable "Related lead" picker. `value` = {id, name} | null.
// Typed text that isn't picked from the list is never treated as a lead.
export const LeadLookupField = ({ value, onChange, onTextChange, testid = "related-lead" }) => {
  const { token } = useAuth();
  const [text, setText] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => { setText(value?.name || ""); }, [value?.id, value?.name]);

  useEffect(() => {
    const term = text.trim();
    if (value || term.length < 2) { setOpen(false); setStatus("idle"); return; }
    const myReq = ++reqRef.current;
    setOpen(true); setStatus("loading");
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/leads/lookup`, { params: { q: term, limit: 10 }, headers: { Authorization: `Bearer ${token}` } });
        if (myReq !== reqRef.current) return;
        setResults(Array.isArray(r.data) ? r.data : []);
        setHi(0); setStatus("done");
      } catch {
        if (myReq === reqRef.current) setStatus("error");
      }
    }, 300);
    return () => clearTimeout(t);
  }, [text, value, token]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const type = (v) => {
    setText(v);
    onTextChange?.(v);
    if (value) onChange(null);
  };
  const pick = (r) => {
    onChange({ id: r.id, name: r.name });
    setText(r.name); onTextChange?.(r.name); setOpen(false);
  };
  const clear = () => { onChange(null); setText(""); onTextChange?.(""); setOpen(false); };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && results[hi]) { e.preventDefault(); pick(results[hi]); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }
  };

  const unpicked = !value && text.trim() && !open;
  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <Search style={{ position: "absolute", left: 9, top: 9, width: 13, height: 13, color: "var(--text-tertiary, #94a3b8)", pointerEvents: "none" }} />
      <input className="input" value={text} onChange={(e) => type(e.target.value)} onKeyDown={onKey}
        onFocus={() => { if (!value && text.trim().length >= 2) setOpen(true); }}
        placeholder="Search leads by practice or contact…" data-testid={`${testid}-input`}
        style={{ width: "100%", paddingLeft: 28, paddingRight: 28, fontWeight: value ? 600 : 400 }} autoComplete="off" />
      {text && (
        <button type="button" onClick={clear} aria-label="Clear related lead" data-testid={`${testid}-clear`}
          style={{ position: "absolute", right: 6, top: 6, background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary, #94a3b8)", display: "flex", padding: 2 }}>
          <X style={{ width: 13, height: 13 }} />
        </button>
      )}
      {open && (
        <div data-testid={`${testid}-dropdown`} style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 50, background: "#fff", border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,0.12)", maxHeight: 260, overflowY: "auto" }}>
          {status === "loading" && <DropNote testid={`${testid}-loading`}><Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> Searching…</DropNote>}
          {status === "error" && <DropNote testid={`${testid}-error`}>Couldn't search leads — try again</DropNote>}
          {status === "done" && !results.length && <DropNote testid={`${testid}-empty`}>No leads found</DropNote>}
          {status === "done" && results.map((r, i) => (
            <LeadOption key={r.id} r={r} active={i === hi} onHover={() => setHi(i)} onPick={() => pick(r)} testid={`${testid}-option-${i}`} />
          ))}
        </div>
      )}
      {unpicked && (
        <div data-testid={`${testid}-unpicked-note`} style={{ fontSize: 10, color: "#B45309", marginTop: 4 }}>
          Pick a lead from the list or clear the field
        </div>
      )}
    </div>
  );
};

const DropNote = ({ children, testid }) => (
  <div data-testid={testid} style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-tertiary, #64748b)", display: "flex", alignItems: "center", gap: 6 }}>{children}</div>
);

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const LeadOption = ({ r, active, onHover, onPick, testid }) => {
  const sub = [r.contact_name !== r.name ? r.contact_name : "", r.specialty, [r.city, r.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
  return (
    <div role="option" aria-selected={active} data-testid={testid} onMouseEnter={onHover}
      onMouseDown={(e) => { e.preventDefault(); onPick(); }}
      style={{ padding: "7px 12px", cursor: "pointer", background: active ? "var(--blue-light, #eff6ff)" : "transparent", transition: "background-color 120ms ease" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary, #0f172a)", ...ellipsis }}>{r.name || "Unnamed lead"}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--text-tertiary, #64748b)", marginTop: 1, ...ellipsis }}>{sub}</div>}
    </div>
  );
};
